import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { buildBackendLoginUrl, currentReturnPath, fetchAuthSession, logoutAuthSession, preferredUserLabel } from '@aide/auth-client'
import type { AuthSessionData } from '@aide/platform-types'
import api from '@/services/api'

interface AuthContextType {
  authenticated: boolean
  user: string | null
  profile: AuthSessionData | null
  loading: boolean
  login: (nextPath?: string, loginHint?: string) => Promise<void>
  signup: (nextPath?: string, loginHint?: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<AuthSessionData | null>(null)
  const [loading, setLoading] = useState(true)

  const clearAuth = useCallback(() => {
    setProfile(null)
    api.setToken(null)
  }, [])

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const session = await fetchAuthSession()
      if (!session) {
        clearAuth()
        return
      }
      setProfile(session)
    } catch {
      clearAuth()
    } finally {
      setLoading(false)
    }
  }, [clearAuth])

  useEffect(() => { void refresh() }, [refresh])

  const login = useCallback(async (nextPath?: string, loginHint?: string) => {
    window.location.assign(buildBackendLoginUrl('forge', nextPath || currentReturnPath(), window.location.origin, '/api', undefined, loginHint))
  }, [])

  const signup = useCallback(async (nextPath?: string, loginHint?: string) => {
    window.location.assign(buildBackendLoginUrl('forge', nextPath || currentReturnPath(), window.location.origin, '/api', 'signup', loginHint))
  }, [])

  const logout = useCallback(async () => {
    try {
      await logoutAuthSession()
    } finally {
      clearAuth()
    }
  }, [clearAuth])

  return <AuthContext.Provider value={{ authenticated: Boolean(profile?.authenticated), user: preferredUserLabel(profile), profile, loading, login, signup, logout }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
